import { Icon } from "@constellation-academy/academies-lib-webapp/components/icon";
import { IconMap } from "@constellation-academy/epic-ui";
import { useMemo, useState } from "react";
import { useFragment } from "react-relay";
import {
	correctAnswerWrapper,
	wrongAnswerTitleClass,
} from "@screens/elearning-content-submission/parts/lesson-insights/lesson-insights.styles";
import { success100 } from "@themes/colors";
import { H3Span, P2Span } from "@themes/font-tags";
import { ORDER_LEARN_ELEMENT_FRAGMENT } from "./order-learn-element.graphql";
import {
	answerStatusColor,
	cardItemWrapperClass,
	listClass,
	orderLearnCorrectAnswerContainer,
	orderLearnCorrectAnswerContent,
	orderLearnCorrectAnswerIconWrapper,
	orderLearnCorrectAnswerTitle,
	orderLearnCorrectAnswerWrapper,
	wrongAnswerSpacing,
} from "./order-learn-element.styles";
import type { OrderElementMap, OrderLearnElementProps } from "./order-learn-element.types";

export const OrderLearnElement = ({
	isResultWrong,
	elementIds,
	lastSelectedOrder,
	getStatusForIndexAndElementId,
	learnElementFragmentRef,
}: OrderLearnElementProps) => {
	const element = useFragment(ORDER_LEARN_ELEMENT_FRAGMENT, learnElementFragmentRef ?? null);
	const [isCorrectShown, setIsCorrectShown] = useState(false);
	const elementsMappedById: OrderElementMap = useMemo(() => {
		return (
			element?.orderItems?.reduce(
				(current, value) => ({ ...current, [value.id]: value }),
				{},
			) || {}
		);
	}, [element?.orderItems]);
	const items = elementIds ? elementIds : element?.orderItems?.map((e) => e.id) || [];

	const orderMap = new Map(items.map((id, index) => [id, index]));

	const correctOrder = [...(element?.orderItems ?? [])].sort(
		(a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity),
	);

	const orderedItems = useMemo(() => {
		if (!isResultWrong) return element?.orderItems ?? [];

		const itemMap = element?.orderItems?.reduce<
			Record<string, typeof element.orderItems[number]>
		>((acc, item) => {
			acc[item.id] = item;
			return acc;
		}, {});

		return lastSelectedOrder?.map((id) => itemMap?.[id]).filter(Boolean);
	}, [isResultWrong, element, lastSelectedOrder]);

	const answers = isResultWrong ? orderedItems : correctOrder;
	const handleAnswerClick = () => {
		if (isResultWrong) {
			setIsCorrectShown((prev) => !prev);
		}
	};

	const handleHideCorrectAnswers = () => setIsCorrectShown(false);

	const createOrderItem = (id: string, index: number, forceStatus?: string) => {
		const item = elementsMappedById[id];
		const status =
			forceStatus ||
			(getStatusForIndexAndElementId
				? getStatusForIndexAndElementId(index, item.id)
				: "default");
		return (
			<div className={cardItemWrapperClass} key={item?.id} onClick={handleAnswerClick}>
				<H3Span>{index + 1}</H3Span>
				<P2Span
					className={answerStatusColor({
						status: status as "actuallyCorrect" | "correct",
					})}
				>
					{item.text}
				</P2Span>
			</div>
		);
	};

	return (
		<>
			<div className={listClass}>
				<div className={wrongAnswerSpacing}>
					<h6 className={wrongAnswerTitleClass}>Deine Antwort</h6>
				</div>
				{answers
					?.map((element) => element?.id)
					.filter(Boolean)
					?.map((id, index) => createOrderItem(String(id), index))}
			</div>

			{isCorrectShown && (
				<div
					className={`${correctAnswerWrapper} ${orderLearnCorrectAnswerWrapper}`}
					onClick={handleHideCorrectAnswers}
				>
					<div className={orderLearnCorrectAnswerContainer}>
						<div className={orderLearnCorrectAnswerIconWrapper}>
							<Icon
								icon={"arrowUp" as keyof IconMap}
								color={success100}
								sizeRem={0.875}
							/>
						</div>

						<div className={orderLearnCorrectAnswerContent}>
							<h6 className={orderLearnCorrectAnswerTitle}>Lösung</h6>
							{correctOrder?.map((item, index) =>
								createOrderItem(item.id, index, "actuallyCorrect"),
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};
